/**
 * 생성기 직접 생성(`direct_generate`) 턴의 요청 wire 계약 —
 * zep-quiz-ai docs/architecture/conversational-api-direct-generate.md.
 * snake_case 그대로 둔다. 보내기 전에 검증해 서버 422 를 미리 막는다.
 */
import { z } from 'zod';

import { MAX_QUIZ_COUNT } from '../../../constant';
import {
  AI_GENERATE_DOCK_SOURCE_TYPES,
  AI_GENERATE_DOCK_TOPIC_MAX_LENGTH,
  type AiGenerateDockSourceType,
} from '../consts/aiGenerateDock.const';

import {
  type AiBuilderDifficultyHint,
  type AiBuilderQuizTypeHint,
} from './types';

const QuizTypeSchema = z.enum(['ox', 'multiple', 'subjective', 'essay']);

const DifficultySchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal('mixed'),
]);

export const DirectGeneratePayloadSchema = z
  .object({
    source_type: z.enum(AI_GENERATE_DOCK_SOURCE_TYPES),
    topic: z.string().max(AI_GENERATE_DOCK_TOPIC_MAX_LENGTH).nullable(),
    quiz_types: z.array(QuizTypeSchema).min(1),
    count: z.number().int().min(1).max(MAX_QUIZ_COUNT),
    difficulty: DifficultySchema,
  })
  .superRefine((payload, ctx) => {
    if (payload.source_type === 'query' && !payload.topic?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['topic'],
        message: 'topic required for query',
      });
    }
  });

export type DirectGeneratePayloadDto = z.infer<
  typeof DirectGeneratePayloadSchema
>;

type AiGenerateDockConditions = {
  quizTypes: AiBuilderQuizTypeHint[];
  count: number;
  difficulty: AiBuilderDifficultyHint;
};

export type AiGenerateDockSubmission = AiGenerateDockConditions &
  (
    | { sourceType: 'query'; topic: string }
    | {
        sourceType: Exclude<AiGenerateDockSourceType, 'query'>;
        sourceRef: string;
        /** 자료에서 다룰 범위(선택) — 서버에는 topic 으로 간다 */
        scope?: string;
      }
  );

export type AiGenerateDockRequestParts = {
  directGenerate: DirectGeneratePayloadDto;
  sourceRefs: string[];
};

export type BuildDirectGenerateRequestResult =
  | { ok: true; value: AiGenerateDockRequestParts }
  | { ok: false; fields: string[] };

/**
 * 제출 값을 turn 요청의 `direct_generate` · `source_refs` 로 만든다.
 * 자료 탭의 source_ref 는 payload 가 아니라 기존 source_refs 로 실어 보낸다.
 * topic 은 주제 탭에선 필수, 자료 탭에선 다룰 범위(선택)다.
 */
export const buildDirectGenerateRequest = (
  submission: AiGenerateDockSubmission,
): BuildDirectGenerateRequestResult => {
  const isQuery = submission.sourceType === 'query';
  const sourceRef = isQuery ? '' : submission.sourceRef.trim();
  const topic = isQuery
    ? submission.topic.trim()
    : submission.scope?.trim() || null;

  const parsed = DirectGeneratePayloadSchema.safeParse({
    source_type: submission.sourceType,
    topic,
    quiz_types: submission.quizTypes,
    count: submission.count,
    difficulty: submission.difficulty,
  });

  const fields = parsed.success
    ? []
    : parsed.error.issues.map(issue => String(issue.path[0]));
  if (!isQuery && !sourceRef) fields.push('source_refs');

  if (!parsed.success || fields.length > 0) {
    return { ok: false, fields: [...new Set(fields)] };
  }

  return {
    ok: true,
    value: {
      directGenerate: parsed.data,
      sourceRefs: isQuery ? [] : [sourceRef],
    },
  };
};
